import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FunnelLayout } from '../../components/FunnelLayout';
import { BreedPhoto } from '../../components/funnel/BreedPhoto';
import { useForm } from '../../context/FormContext';
import { useAppConfig } from '../../context/AppConfigContext';
import { calculateNutritionPreview, CalculationPreview } from '../../services/api';
import { DogFormData, Protein } from '../../types';
import { getAvailableRecipes } from '../../utils/products';
import {
  CALC_TIMEOUT_MS,
  FASE_1_STAGES,
  FASE_2_STAGES,
  PHASE_1_DURATION_MS,
  PHASE_2_DURATION_MS,
  RevealContext,
} from './revealStages';
import { buildEstadoBullets } from './p9/buildEstadoBullets';
import { Phase1Panel } from './p9/Phase1Panel';
import { Phase2Panel } from './p9/Phase2Panel';
import { EditConfirmModal } from './p9/EditConfirmModal';

type AnimPhase = 'phase1' | 'phase2' | 'review' | 'done';

interface P9RevealProps {
  onComplete?: () => void;
  backendCalculate?: (formData: Partial<DogFormData>, signal?: AbortSignal) => Promise<CalculationPreview>;
}

const AFINANDO_MAX_BULLETS = 5;
const REVEAL_CACHE_KEY = 'myl_p9_reveal_signature_v1';

function sortedList(values?: unknown[]): string[] {
  return (values || []).map(String).sort();
}

function buildRevealSignature(formData: Partial<DogFormData>): string {
  return JSON.stringify({
    nombre: formData.nombre || '',
    raza: formData.raza || '',
    ageCategory: formData.ageCategory || '',
    ageYears: formData.ageYears ?? null,
    ageMonths: formData.ageMonths ?? null,
    ageTotalMonths: formData.ageTotalMonths ?? null,
    gender: formData.gender || '',
    peso: formData.peso ?? null,
    bodyCondition: formData.bodyCondition || '',
    activityLevel: formData.activityLevel || '',
    isNeutered: formData.isNeutered ?? null,
    currentFood: sortedList(formData.currentFood),
    pathologies: sortedList(formData.pathologies),
    proteinAllergies: sortedList(formData.proteinAllergies),
  });
}

function hasSeenReveal(signature: string): boolean {
  try {
    return window.localStorage.getItem(REVEAL_CACHE_KEY) === signature;
  } catch {
    return false;
  }
}

function saveSeenReveal(signature: string) {
  try {
    window.localStorage.setItem(REVEAL_CACHE_KEY, signature);
  } catch {
    // Non-critical UX cache.
  }
}

function P9Reveal({ onComplete, backendCalculate = calculateNutritionPreview }: P9RevealProps) {
  const { formData, updateFormData, setCurrentStep, goToNextStep } = useForm();
  const { t, config, recipes } = useAppConfig();
  const revealSignature = buildRevealSignature(formData as Partial<DogFormData>);
  const shouldFastReveal = hasSeenReveal(revealSignature);

  const [editStageKey, setEditStageKey] = useState<string | null>(null);
  const [visibleFase1, setVisibleFase1] = useState<number>(shouldFastReveal ? FASE_1_STAGES.length : 0);
  const [visibleFase2, setVisibleFase2] = useState<number>(shouldFastReveal ? FASE_2_STAGES.length : 0);
  const [animPhase, setAnimPhase] = useState<AnimPhase>(shouldFastReveal ? 'review' : 'phase1');
  const [preview, setPreview] = useState<CalculationPreview | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  // "Recetas idóneas" = compatibles con el perro (etapa + alergias), ignorando
  // stock (las agotadas cuentan; P10 las muestra en gris). Mismo filtro que Step10
  // para que el número del reveal cuadre con las cards del selector.
  const compatibleRecipeCount = useMemo(() => {
    const targetLifeStage = String(formData.ageCategory || '') === 'cachorro' ? 'puppy' : 'adult';
    const pool = (recipes ?? []).filter(
      (r) => !r.lifeStage || r.lifeStage === targetLifeStage || r.lifeStage === 'all',
    );
    const filtered = getAvailableRecipes(formData.proteinAllergies || [Protein.NINGUNA], pool, {
      proteinAliasLookup: config.exclusion_rules?.protein_aliases,
      pathologies: formData.pathologies,
      subPathologies: formData.subPathologies,
      exclusionRules: config.exclusion_rules || {},
    });
    return filtered.filter((r) => !r.isExcluded).length;
  }, [
    recipes,
    formData.ageCategory,
    formData.proteinAllergies,
    formData.pathologies,
    formData.subPathologies,
    config.exclusion_rules,
  ]);
  const nombre = formData.nombre || 'tu perro';

  const handleComplete = useCallback(() => {
    if (onComplete) {
      onComplete();
    } else {
      goToNextStep();
    }
  }, [onComplete, goToNextStep]);

  // effectivePreview: solo el valor real del backend. Sin fallback aproximado.
  // Mientras el backend no responda (incluyendo timeout), los resultados
  // muestran el estado de carga (shimmer) en Phase2Panel.
  const effectivePreview: CalculationPreview | null = preview;

  const mountedRef = useRef(true);
  const completedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    completedRef.current = false;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (animPhase === 'review') saveSeenReveal(revealSignature);
  }, [animPhase, revealSignature]);

  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      if (mountedRef.current) setTimedOut(true);
    }, CALC_TIMEOUT_MS);

    const promise = backendCalculate(
      { ...formData, platform: config.effective_platform } as Partial<DogFormData>,
      controller.signal,
    );
    // Guard: backendCalculate may be undefined in some test environments where the
    // services/api module mock chain fails to initialize. Fall back to timedOut.
    if (!promise || typeof promise.then !== 'function') {
      clearTimeout(timeoutId);
      if (mountedRef.current) setTimedOut(true);
    } else {
      promise
        .then((result) => {
          clearTimeout(timeoutId);
          if (!mountedRef.current) return;
          setPreview(result);
          // Persist the backend DER so Step10's stock heads-up can estimate the
          // plan's cans on the first forward pass. P9 already fetches it here, so
          // no extra call; Step11 re-persists it with the full plan later.
          if (result.daily_kcal > 0) {
            updateFormData({ dailyKcal: Math.round(result.daily_kcal) });
          }
        })
        .catch(() => {
          clearTimeout(timeoutId);
          if (mountedRef.current) setTimedOut(true);
        });
    }

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (animPhase !== 'phase1') return;

    const timers: ReturnType<typeof setTimeout>[] = [];

    FASE_1_STAGES.forEach((stage, idx) => {
      timers.push(
        setTimeout(() => {
          if (mountedRef.current) setVisibleFase1(idx + 1);
        }, stage.delayMs + stage.durationMs),
      );
    });

    timers.push(
      setTimeout(() => {
        if (mountedRef.current) setAnimPhase('phase2');
      }, PHASE_1_DURATION_MS),
    );

    return () => timers.forEach(clearTimeout);
  }, [animPhase]);

  useEffect(() => {
    if (animPhase !== 'phase2') return;

    const timers: ReturnType<typeof setTimeout>[] = [];

    FASE_2_STAGES.forEach((stage, idx) => {
      timers.push(
        setTimeout(() => {
          if (mountedRef.current) setVisibleFase2(idx + 1);
        }, stage.delayMs + stage.durationMs),
      );
    });

    timers.push(
      setTimeout(() => {
        if (!mountedRef.current) return;
        setAnimPhase('review');
      }, PHASE_2_DURATION_MS),
    );

    return () => timers.forEach(clearTimeout);
  }, [animPhase]);

  const handleConfirm = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    setAnimPhase('done');
    handleComplete();
  }, [handleComplete]);

  const breedEntry = formData.raza ? config.breed_database?.breeds?.[formData.raza] : undefined;
  const breedDisplayName = breedEntry?.display_name;

  const revealCtx: RevealContext = {
    nombre,
    raza: formData.raza,
    breedDisplayName,
    ageYears: formData.ageYears,
    ageCategory: formData.ageCategory,
    gender: formData.gender,
    peso: formData.peso,
    bodyCondition: formData.bodyCondition,
    activityLevel: formData.activityLevel,
    currentFood: formData.currentFood ?? [],
    pathologies: formData.pathologies ?? [],
    allergies: formData.proteinAllergies?.filter((a) => a !== 'ninguna') ?? [],
    daily_kcal: preview?.daily_kcal,
    daily_grams: preview?.daily_grams,
    recipe_count: preview?.offerable_recipe_count,
    supplement_count: preview?.supplement_suggestion_count,
  };

  const estadoBullets = useMemo(
    () =>
      buildEstadoBullets({
        nombre,
        breedDisplayName,
        breedSize: breedEntry?.breed_size,
        ageCategory: formData.ageCategory,
        bodyCondition: formData.bodyCondition,
        activityLevel: formData.activityLevel,
        allergies: formData.proteinAllergies ?? [],
        pathologies: formData.pathologies ?? [],
        t,
      }).slice(0, AFINANDO_MAX_BULLETS),
    [
      nombre,
      breedDisplayName,
      breedEntry,
      formData.ageCategory,
      formData.bodyCondition,
      formData.activityLevel,
      formData.proteinAllergies,
      formData.pathologies,
      t,
    ],
  );

  const isPostCalc = animPhase === 'review' || animPhase === 'done';
  const bulletsToShow = estadoBullets;

  const visibleAfinando = useMemo(() => {
    if (isPostCalc) return bulletsToShow.length;
    if (animPhase !== 'phase2') return 0;
    const ratio = visibleFase2 / Math.max(1, FASE_2_STAGES.length);
    return Math.min(bulletsToShow.length, Math.max(1, Math.round(ratio * bulletsToShow.length)));
  }, [animPhase, isPostCalc, visibleFase2, bulletsToShow.length]);

  const afinandoBulletsList =
    isPostCalc && bulletsToShow.length > 0 ? (
      <ul className="space-y-2.5 mt-3 pt-3 border-t border-[#b3e0f0]/60">
        {bulletsToShow.map((b, idx) => {
          const visible = idx < visibleAfinando;
          return (
            <li
              key={b.key}
              className={`flex items-start gap-2.5 transition-all duration-500 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'
              }`}
              aria-hidden={!visible}
            >
              <span
                className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: '#008BBC' }}
                aria-hidden="true"
              />
              <p className="text-sm text-gray-700 leading-snug">{b.text}</p>
            </li>
          );
        })}
      </ul>
    ) : null;

  const showPhase1Panel = true;
  const showPhase2 = animPhase === 'phase2' || animPhase === 'review' || animPhase === 'done';
  const isReview = animPhase === 'review';

  return (
    <FunnelLayout
      step={9}
      useTwoPhase
      maxWidth="5xl"
      showBack
      showContinue
      onContinue={handleConfirm}
      continueDisabled={!isReview}
      continueDisabledReason={!isReview ? t('p9.bottom_disabled_reason', 'Calculando el plan…') : undefined}
    >
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(144,209,232,0.18) 0%, transparent 70%)',
          zIndex: 0,
        }}
      />

      <div
        className="relative flex flex-col items-center min-h-[calc(60vh-2rem)] gap-3 px-3 pb-4 pt-2 sm:min-h-[60vh] sm:gap-5 sm:px-4 sm:py-8 max-w-[480px] lg:max-w-none mx-auto"
        style={{ zIndex: 1 }}
      >
        <div
          className="transition-all duration-700 scale-100"
          style={{ animation: 'dogFloat 3s ease-in-out infinite' }}
        >
          <BreedPhoto
            breedKey={formData.raza}
            alt={nombre}
            className="w-20 h-20 sm:w-24 sm:h-24 drop-shadow-md select-none"
          />
        </div>

        <div className="min-h-[58px] text-center sm:min-h-[62px]">
          <h2 className="font-fredoka text-lg font-bold leading-tight text-gray-900 sm:text-xl">
            {isReview
              ? t('p9.review_title', 'Plan de {nombre} listo', { nombre })
              : animPhase === 'phase1'
                ? t('p9.title', 'Calculando el plan de {nombre}…', { nombre })
                : t('p9.phase2_title', 'Calculando el plan de {nombre}…', { nombre })}
          </h2>
          <p className="mt-1 text-xs leading-snug text-gray-400">
            {isReview
              ? t('p9.review_subtitle', 'Revisa los datos antes de elegir las recetas')
              : animPhase === 'phase1'
                ? t('p9.phase1_subtitle', 'Recopilando datos de {nombre}', { nombre })
                : timedOut && !preview
                  ? t('p9.phase2_subtitle_timeout', 'Tarda más de lo normal — afinando los últimos datos…')
                  : t('p9.phase2_subtitle', 'Personalizando cada detalle')}
          </p>
        </div>

        <div className="w-full flex flex-col gap-5 lg:grid lg:grid-cols-2 lg:gap-5 lg:items-stretch">
          {showPhase1Panel && (
            <Phase1Panel
              visibleFase1={visibleFase1}
              isReview={isReview}
              revealCtx={revealCtx}
              nombre={nombre}
              t={t}
              onEditStage={setEditStageKey}
            />
          )}

          {showPhase2 && (
            <Phase2Panel
              visibleFase2={visibleFase2}
              effectivePreview={effectivePreview}
              availableRecipesCount={compatibleRecipeCount}
              availableSupplementsCount={config.supplement_rules?.supplements?.length ?? 0}
              timedOut={timedOut}
              preview={preview}
              t={t}
              afinandoBulletsList={afinandoBulletsList}
            />
          )}
        </div>
      </div>

      <style>{`
        @keyframes dogFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        @keyframes dogBounce {
          0% { transform: scale(1); }
          40% { transform: scale(1.15); }
          70% { transform: scale(0.97); }
          100% { transform: scale(1.08); }
        }
      `}</style>

      {editStageKey && (
        <EditConfirmModal
          stageKey={editStageKey}
          onCancel={() => setEditStageKey(null)}
          onConfirm={(target) => {
            setEditStageKey(null);
            setCurrentStep(target);
          }}
        />
      )}
    </FunnelLayout>
  );
}

export default P9Reveal;
